import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import moment from "moment";
import { AppDispatch } from "../../redux/store";
import { applyBundleSelection, fetchActivePlan, hasScheduledMigration, setMigrationFlowOpen } from "../../redux/slices/activePlanSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import useEnhancedNavigate from "../Session/helpers";
import DeleteModal from "../Shared/DeleteModal";
import { formatMoney } from "./checkoutPreview";
import {
  BASE_PROMPTS_PER_DAY,
  BUNDLE_CATALOGUE,
  CatalogueBundle,
  bundlePrice,
  catalogueFromApi,
  formatUsd,
} from "./bundleCatalogue";

const BundleSettings = ({ onClose }: { onClose: () => void }) => {
  const navigate = useEnhancedNavigate();
  const dispatch: AppDispatch = useDispatch();
  const apiPlans = useSelector((state: { plans }) => state.plans.plans);
  const plan = useSelector((state: { plan }) => state.plan.plan);
  const [pendingRemove, setPendingRemove] = useState<CatalogueBundle | null>(null);
  const [removing, setRemoving] = useState(false);

  const catalogue = useMemo(
    () => catalogueFromApi(apiPlans)
      ?? { bundles: BUNDLE_CATALOGUE, basePrompts: BASE_PROMPTS_PER_DAY },
    [apiPlans],
  );

  const ownedIds: string[] = useMemo(() => plan?.ownedBundles ?? [], [plan]);
  const ownedBundles = useMemo(
    () => {
      const order = new Map(catalogue.bundles.map((b, i) => [b.id, i]));
      return ownedIds
        .map(id =>
          catalogue.bundles.find(b => b.id === id)
          ?? { id, name: id, tagline: "", priceMonthly: null, features: [] } as CatalogueBundle,
        )
        .sort((a, b) => (order.get(a.id) ?? Infinity) - (order.get(b.id) ?? Infinity));
    },
    [ownedIds, catalogue],
  );

  const monthlyTotal = plan?.monthlySpend
    ?? ownedBundles.reduce((sum, b) => sum + (b.priceMonthly ?? 0), 0);
  const dailyPrompts = plan?.dailyPrompts
    ?? catalogue.basePrompts
    + ownedBundles.reduce((sum, b) => sum + (b.promptsPerDay ?? b.priceMonthly ?? 0), 0);
  const earnedPrompts = Math.max(0, dailyPrompts - catalogue.basePrompts);

  const expiresAt = plan?.subscription?.expires_at;
  const renewalLabel = plan?.non_renewing ? "Expires" : "Renews";

  const billingPeriod = plan?.billingPeriod ?? plan?.subscription?.billing_period ?? "month";
  const isYearly = billingPeriod === "year";

  const spendAtCadence = (list: CatalogueBundle[]) =>
    Math.round(list.reduce((sum, b) => sum + (bundlePrice(b, isYearly) ?? 0), 0) * 100) / 100;
  const cadenceSuffix = isYearly ? "/year" : "/month";

  const currentSpend = isYearly ? spendAtCadence(ownedBundles) : monthlyTotal;

  const isManualGrant = plan?.subscription?.provider === "manual";
  const deferredToRenewal = !isManualGrant;

  const isTradeAlgoComped = plan?.comped === true
    && (plan?.subscription_provider ?? plan?.subscription?.provider) === "trade_algo";

  const legacyPlan = plan?.legacyPlan;
  const migrationScheduled = hasScheduledMigration(plan);
  const scheduledBundleCount = plan?.scheduledChanges?.bundles?.length ?? 0;
  const perBundleBilling = !isTradeAlgoComped && !legacyPlan;

  const ownsAllBundles = catalogue.bundles
    .filter(b => b.priceMonthly != null)
    .every(b => ownedIds.includes(b.id));
  const renewalDate = expiresAt ? moment(expiresAt).format("DD MMM YYYY") : null;
  const effectiveClause = renewalDate ? `on ${renewalDate}` : "at your next renewal";
  const modalDate = expiresAt ? moment(expiresAt).format("YYYY-MM-DD") : null;

  const endingIds: string[] = plan?.endingBundles ?? [];
  const scheduledEndDate = renewalDate;

  const nextTermIds = ownedIds.filter(id => !endingIds.includes(id));
  const nextTermBundles = ownedBundles.filter(b => nextTermIds.includes(b.id));
  const desiredAfterRemove = pendingRemove
    ? nextTermIds.filter(id => id !== pendingRemove.id)
    : nextTermIds;
  const removalCancelsPlan = !!pendingRemove && desiredAfterRemove.length === 0;

  const endingBundleNames = ownedBundles
    .filter(b => endingIds.includes(b.id))
    .map(b => b.name);
  const nextTermPrompts = catalogue.basePrompts
    + nextTermBundles.reduce((sum, b) => sum + (b.promptsPerDay ?? b.priceMonthly ?? 0), 0);

  const renewalCharge = plan?.renewalCharge;
  const nextTermTotalLabel = renewalCharge
    ? formatMoney(renewalCharge.amount, renewalCharge.currency)
    : formatUsd(spendAtCadence(nextTermBundles));

  const goToPlans = () => {
    onClose();
    navigate("/plans");
  };

  const openMigrationFlow = () => {
    onClose();
    dispatch(setMigrationFlowOpen(true));
  };

  const handleRemove = async () => {
    if (!pendingRemove) return;
    setRemoving(true);
    try {
      await dispatch(
        applyBundleSelection({ bundleIds: desiredAfterRemove }),
      ).unwrap();

      dispatch(fetchActivePlan({ silent: true }));
      dispatch(showNotification({
        message: removalCancelsPlan
          ? `${pendingRemove.name} will be removed ${effectiveClause} — your plan then drops to Free.`
          : deferredToRenewal
            ? `${pendingRemove.name} will be removed ${effectiveClause}.`
            : `${pendingRemove.name} removed from your plan.`,
        severity: "success",
        horizontal: "right",
      }));
    }
    catch {
      dispatch(showNotification({ message: "Couldn't remove the bundle, please try again.", severity: "error", horizontal: "right" }));
    }
    finally {
      setRemoving(false);
      setPendingRemove(null);
    }
  };

  const survivingBundles = pendingRemove
    ? nextTermBundles.filter(b => b.id !== pendingRemove.id)
    : nextTermBundles;
  const promptsAfterRemove = catalogue.basePrompts
    + survivingBundles.reduce((sum, b) => sum + (b.promptsPerDay ?? b.priceMonthly ?? 0), 0);
  const totalAfterRemove = spendAtCadence(survivingBundles);

  const toolCount = pendingRemove?.features.length ?? 0;
  const removeIntro = deferredToRenewal
    ? `You'll keep access to ${toolCount} tool${toolCount === 1 ? "" : "s"} until ${modalDate ?? "your next renewal"}. After that, these tools will no longer be available in your prompts:`
    : "Removal applies immediately. These tools will no longer be available in your prompts:";
  const planAfterLabel = deferredToRenewal ? `Your plan after ${modalDate ?? "your next renewal"}` : "Your plan now";

  const bundleCaption = (bundle: CatalogueBundle) => {
    const parts = [];
    if (bundle.features.length > 0) parts.push(`${bundle.features.length} tools`);
    const price = bundlePrice(bundle, billingPeriod === "year");
    if (price != null) parts.push(`${formatUsd(price)}/${billingPeriod === "year" ? "yr" : "mo"}`);
    return parts.join(" · ");
  };

  return (
    <div className="w-full max-w-lg text-mui-black-87 dark:text-white">
      <Typography variant="h6/medium-500" component="h6" className="!mb-1">
        Your bundles
      </Typography>
      <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56 !mb-4">
        {dailyPrompts}
        {" "}
        prompts/day

        {perBundleBilling && (
          <>
            {" "}
            (
            {catalogue.basePrompts}
            {" "}
            base +
            {" "}
            {earnedPrompts}
            {" "}
            from bundles)
          </>
        )}
      </Typography>

      {ownedBundles.length === 0
        ? (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 py-8 px-4 text-center">
              <Typography variant="body2/regular-400" component="p" className="text-mui-black-60 dark:text-mui-white-56 !mb-3">
                No bundles yet. Add one to unlock tools and earn more prompts.
              </Typography>
              <Button
                variant="contained"
                onClick={goToPlans}
                className="!bg-black dark:!bg-white !text-white dark:!text-mui-black-87 !normal-case !py-1.5 !px-6"
              >
                Browse bundles
              </Button>
            </div>
          )
        : (
            <div className="flex flex-col">
              {ownedBundles.map((bundle) => {
                const isEnding = endingIds.includes(bundle.id);
                return (
                  <div
                    key={bundle.id}
                    className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <Typography
                        variant="body2/regular-400"
                        className={isEnding
                          ? "!font-medium text-mui-black-60 dark:text-mui-white-56"
                          : "!font-medium"}
                      >
                        {bundle.name}
                      </Typography>
                      {isEnding
                        ? (
                            <Typography variant="caption" component="p" className="text-warning-main">
                              {bundle.features.length}
                              {" "}
                              tools · Ends
                              {" "}
                              {scheduledEndDate}
                            </Typography>
                          )
                        : (
                            <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                              {bundleCaption(bundle)}
                            </Typography>
                          )}
                    </div>

                    {perBundleBilling && isEnding && (
                      <Typography variant="caption" className="text-warning-main shrink-0">
                        Ending
                      </Typography>
                    )}

                    {perBundleBilling && !isEnding && (
                      <button
                        type="button"
                        aria-label={`Remove ${bundle.name}`}
                        onClick={() => setPendingRemove(bundle)}
                        disabled={removing}
                        className="text-sm font-medium text-mui-black-60 dark:text-mui-white-56 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-0 p-0 cursor-pointer"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div>
          {isTradeAlgoComped
            ? (
                <>
                  <Typography variant="body2/regular-400" className="!font-medium">
                    Included with TradeAlgo
                  </Typography>
                  <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                    Your bundles are linked to your active TradeAlgo subscription.
                  </Typography>
                </>
              )
            : legacyPlan
              ? (
                  <>
                    <Typography variant="body2/regular-400" className="!font-medium">
                      {formatUsd(legacyPlan.price_monthly)}
                      /mo
                    </Typography>
                    <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                      TradeGPT
                      {" "}
                      {legacyPlan.type}
                      {" "}
                      — legacy plan
                    </Typography>
                    {migrationScheduled
                      ? (
                          <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                            Switching to
                            {" "}
                            {scheduledBundleCount}
                            {" "}
                            {scheduledBundleCount === 1 ? "bundle" : "bundles"}
                            {" on "}
                            {moment(plan?.scheduledChanges?.effective_at ?? legacyPlan.migrate_by).format("DD MMM YYYY")}
                          </Typography>
                        )
                      : (
                          <Typography variant="caption" component="p" className="text-warning-main">
                            Moving to bundles — choose yours by
                            {" "}
                            {moment(legacyPlan.migrate_by).format("DD MMM YYYY")}
                          </Typography>
                        )}
                  </>
                )
              : (
                  <>
                    <Typography variant="body2/regular-400" className="!font-medium">
                      {formatUsd(currentSpend)}
                      /
                      {isYearly ? "yr" : "mo"}
                    </Typography>
                    {expiresAt && (
                      <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                        {renewalLabel}
                        {" "}
                        {moment(expiresAt).format("DD MMM YYYY")}

                        {!plan?.non_renewing && renewalCharge && renewalCharge.amount > 0 && (
                          <>
                            {" · "}
                            {formatMoney(renewalCharge.amount, renewalCharge.currency)}
                            {cadenceSuffix}
                          </>
                        )}
                      </Typography>
                    )}
                  </>
                )}
        </div>
        {legacyPlan
          ? (
              <Button
                variant="outlined"
                onClick={migrationScheduled ? goToPlans : openMigrationFlow}
                className="!normal-case !border-tgpt-secondary-states-outlinedBorder !text-tgpt-secondary-main hover:!bg-tgpt-secondary-states-hover"
              >
                {migrationScheduled ? "Change bundles" : "Choose bundles"}
              </Button>
            )
          : !ownsAllBundles && (
              <Button
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={goToPlans}
                className="!normal-case !border-tgpt-secondary-states-outlinedBorder !text-tgpt-secondary-main hover:!bg-tgpt-secondary-states-hover"
              >
                Add bundles
              </Button>
            )}
      </div>

      {perBundleBilling && endingIds.length > 0 && (
        <div className="mt-4 pt-4 border-t border-dashed border-warning-main flex flex-col gap-y-1">
          <Typography variant="caption" component="p" className="text-warning-main uppercase !tracking-wide">
            After
            {" "}
            {scheduledEndDate ?? "your next renewal"}
          </Typography>
          {endingBundleNames.map(name => (
            <Typography key={name} variant="body2/regular-400" component="p" className="text-mui-black-60 dark:text-mui-white-56">
              {name}
              {" "}
              will end
            </Typography>
          ))}
          <Typography variant="body1/regular-600" component="p">
            {nextTermPrompts}
            {" "}
            prompts/day for
            {" "}
            {nextTermTotalLabel}
            {cadenceSuffix}
          </Typography>
        </div>
      )}

      <DeleteModal
        open={!!pendingRemove}
        isDeleting={removing}
        widthClassName="w-modal-mobile sm:w-modal-md"
        handleDelete={handleRemove}
        handleCloseWarning={() => setPendingRemove(null)}
        mainText={`Remove ${pendingRemove?.name ?? "bundle"}?`}
        subText={removeIntro}
        confirmText="Remove bundle"
        cancelText="Keep bundle"
        additionalContent={(
          <div className="flex flex-col gap-y-4">
            {toolCount > 0 && (
              <ul className="list-disc pl-5 flex flex-col gap-y-1">
                {pendingRemove?.features.map(feature => (
                  <li key={feature.name}>
                    <Typography variant="body2/regular-400">{feature.name}</Typography>
                  </li>
                ))}
              </ul>
            )}
            <div className="rounded-lg bg-action-hover px-4 py-3 flex flex-col gap-y-0.5">
              <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56 uppercase !tracking-wide">
                {planAfterLabel}
              </Typography>
              <Typography variant="body1/regular-600" component="p">
                {promptsAfterRemove}
                {" "}
                prompts/day for
                {" "}
                {formatUsd(totalAfterRemove)}
                {cadenceSuffix}
              </Typography>
              <Typography variant="caption" component="p" className="text-mui-black-38 dark:text-mui-white-38 line-through">
                {dailyPrompts}
                {" "}
                prompts/day for
                {" "}
                {formatUsd(currentSpend)}
                {cadenceSuffix}
              </Typography>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default BundleSettings;
